/**
 * 협력사 주정산내역 타입 정의
 * 기반 테이블: vr_supp_acct_weekly
 */
import { formatDateToDisplay } from "@/shared/utils/formatter";

/** 협력사 주정산내역 Domain Model */
export interface PartnerSettlement {
    no: number;                         // 번호 (UI 용)
    supplierName: string;               // 협력사명
    supplierId: string;                 // 협력사ID (SUPPLY_ID)
    businessNumber: string;             // 사업자번호 (별도 기입 예정)
    businessName: string;               // 사업자명 (별도 기입 예정)
    settlementStartDate: string;        // 정산시작일
    settlementEndDate: string;          // 정산종료일
    paymentDate: string;                // 지급일 (정산종료일+2일)
    processCount: number;               // 처리건수
    deliveryFee: number;                // 배달료
    additionalSettlement: number;       // 추가정산
    managementFee: number;              // 관리비
    supplyAmount: number;               // 공급가액
    vat: number;                        // 부가세액
    supplyPrice: number;                // 공급대가 (공급가액 + 부가세액)
    branchOperationFee: number;         // 지점 운영 수수료
    achievementPayback: number;         // 달성 페이백
    promotion: number;                  // 프로모션 (Phase2, 현재 0원)
    paymentManagementFee: number;       // 지급관리비(vat포함) = (관리비 + 지점운영수수료 + 달성페이백 + 프로모션) * 1.1
    dailyDeduction: number;             // 일차감
    employerEmployment: number;         // 사업주_고용
    employerIndustrial: number;         // 사업주_산재
    insuranceTotal: number;             // 보험료합계 (사업주_고용 + 사업주_산재)
    actualPayment: number;              // 실 지급 금액 = 지급관리비(vat포함) + 일차감 - 보험료합계

    // DTO에서 수신하지만 현재 테이블에 표시하지 않는 필드
    // 향후 보험료 상세 내역 또는 소급 정산 화면 구현 시 활용 예정
    riderEmployment?: number;           // 라이더고용
    riderIndustrial?: number;           // 라이더산재
    employerRetroEmployment?: number;   // 사업주소급고용 (소급 정산 기능 예정)
    employerRetroIndustrial?: number;   // 사업주소급산재 (소급 정산 기능 예정)
    withholdingTax?: number;            // 원천세 (세금 상세 표시 예정)
}

/** 협력사 주정산내역 DTO (백엔드 응답) */
export interface PartnerSettlementDto {
    seqNo: number;
    acctStDate: string;
    acctEdDate: string;
    supplyId: string;
    suppName?: string;
    bizRegNo?: string;
    bizName?: string;
    procCnt?: number;
    deliveryVal: number;
    addAcctVal: number;
    maintncVal: number;
    supplyAmt?: number;
    taxVat: number;
    supplyPrice?: number;
    manageFee: number;                  // 지점 운영 수수료
    achievementPayback?: number;        // 달성 페이백
    promotion?: number;                 // 프로모션
    paymentMgmtFee?: number;           // 지급관리비
    deductVal: number;
    suppEmpVal: number;
    riderEmpVal: number;
    suppAccidtVal: number;
    riderAccidtVal: number;
    totInsureVal: number;
    insuranceTotal?: number;
    retEmpVal: number;
    retAccidtVal: number;
    withholdTax: number;
    acctVal: number;
    insDate: string;
    insUser: string;
    updDate: string | null;
    updUser: string | null;
}

/** 협력사 주정산내역 목록 조회 응답 DTO */
export interface GetPartnerSettlementListResponseDto {
    totalCount: number;
    settlements: PartnerSettlementDto[];
}

/** 최근 정산일자 조회 응답 DTO */
export interface GetLatestSettlementDateResponseDto {
    latestStartDate: string;    // 최근 정산시작일자
    latestEndDate: string;      // 최근 정산종료일자
}

/** API 응답 DTO */
export interface ApiResponseDto {
    success: boolean;
    message: string;
}

/**
 * 지급일 계산 함수
 * 정산종료일 + 2일 (달력 기준)
 */
const calculatePaymentDate = (settlementEndDate: string): string => {
    const date = new Date(settlementEndDate.replace(/-/g, '/'));
    date.setDate(date.getDate() + 2);

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
}

/** DTO -> Domain 변환 */
export const partnerSettlementFromDto = (dto: PartnerSettlementDto, index: number): PartnerSettlement => {
    const settlementEndDate = formatDateToDisplay(dto.acctEdDate);
    const paymentDate = calculatePaymentDate(settlementEndDate);

    // 공급가액 계산: 배달료 + 추가정산 + 관리비
    const supplyAmount = (dto.deliveryVal || 0) + (dto.addAcctVal || 0) + (dto.maintncVal || 0);

    // 공급대가 계산: 공급가액 + 부가세액
    const supplyPrice = supplyAmount + (dto.taxVat || 0);

    // 보험료합계 계산: 사업주_고용 + 사업주_산재
    const insuranceTotal = (dto.suppEmpVal || 0) + (dto.suppAccidtVal || 0);

    // 프로모션 (현재 Phase1이므로 0원)
    const promotion = dto.promotion || 0;

    // 지급관리비(vat포함) = (관리비 + 지점운영수수료 + 달성페이백 + 프로모션) * 1.1
    const paymentManagementFee = ((dto.maintncVal || 0) + (dto.manageFee || 0) +
        (dto.achievementPayback || 0) + promotion) * 1.1;

    // 실 지금 금액 = 지급관리비(vat포함) + 일차감 - 보험료합계
    const actualPayment = Math.round(paymentManagementFee) + (dto.deductVal || 0) - insuranceTotal;

    return {
        no: index + 1,
        supplierName: dto.suppName || dto.supplyId,
        supplierId: dto.supplyId,
        businessNumber: dto.bizRegNo || '',
        businessName: dto.bizName || '',
        settlementStartDate: formatDateToDisplay(dto.acctStDate),
        settlementEndDate: settlementEndDate,
        paymentDate: paymentDate,
        processCount: dto.procCnt || 0,
        deliveryFee: dto.deliveryVal || 0,
        additionalSettlement: dto.addAcctVal || 0,
        managementFee: dto.maintncVal || 0,
        supplyAmount: supplyAmount,
        vat: dto.taxVat || 0,
        supplyPrice: supplyPrice,
        branchOperationFee: dto.manageFee || 0,
        achievementPayback: dto.achievementPayback || 0,
        promotion: promotion,
        paymentManagementFee: Math.round(paymentManagementFee),
        dailyDeduction: dto.deductVal || 0,
        employerEmployment: dto.suppEmpVal || 0,
        employerIndustrial: dto.suppAccidtVal || 0,
        insuranceTotal: insuranceTotal,
        actualPayment: actualPayment,

        // 선택적 필드
        riderEmployment: dto.riderEmpVal || 0,
        riderIndustrial: dto.riderAccidtVal || 0,
        employerRetroEmployment: dto.retEmpVal || 0,
        employerRetroIndustrial: dto.retAccidtVal || 0,
        withholdingTax: dto.withholdTax || 0
    };
};

/** 정산내역 목록 변환 */
export const partnerSettlementListFromDto = (
    response: GetPartnerSettlementListResponseDto,
    suppliers?: Array<{ id: string; name: string }>
): PartnerSettlement[] => {
    return response.settlements.map((dto, index) => {
        // 협력사명 매핑
        if (suppliers && !dto.suppName) {
            const supplier = suppliers.find(s => s.id === dto.supplyId);
            if (supplier) {
                dto.suppName = supplier.name;
            }
        }
        return partnerSettlementFromDto(dto, index);
    });
};
